import array from '../../utils/array.js'
import dispatcher from '../../utils/dispatcher.js'
import never from '../../utils/never.js'
import object from '../../utils/object.js'
import unresolved from '../../utils/unresolved.js'
import listener from '../listeners/listener.js'
import once from '../options/once.js'

const subscribe = (
  type,
  { ...options } = {},
  [...hooks] = []
) => {
  const dispatchers = new WeakMap()

  const onEvent = object(listener, {
    ...options,
    type,
    hooks: array(hooks),
    task: (
      currentTarget,
      event
    ) => {
      dispatchers.get(currentTarget)?.dispatch(event)
    }
  })

  return async function* (
    currentTarget,
    aborted
  ) {
    if (dispatchers.has(currentTarget)) {
      throw new Error('Duplicate listener')
    }

    dispatchers.set(currentTarget, object(dispatcher))

    const events = dispatchers.get(currentTarget).subscribe()

    try {
      onEvent.listen(currentTarget)

      while (true) {
        const result = await Promise.race([aborted, events.next()])

        if (await unresolved(aborted)) {
          yield result.value

          if (options.once === once) {
            break
          }
        } else {
          break
        }
      }
    } finally {
      onEvent.forget(currentTarget)
      events.return()
    }
  }
}

export const event = (
  type,
  { ...options } = {},
  [...hooks] = []
) => {
  const subscription = events(type, { ...options, once }, hooks)

  return async (
    currentTarget,
    aborted = never
  ) => {
    for await (const event of subscription(currentTarget, aborted)) {
      return event
    }
  }
}

export const events = (
  type,
  { ...options } = {},
  [...hooks] = []
) => {
  const events = subscribe(type, options, hooks)

  return (
    currentTarget,
    aborted = never
  ) => {
    return events(currentTarget, aborted)
  }
}
